;(() => {
  // --- Garde-fou global : éviter double chargement du fichier
  if (window.__PC_CORE_LOADED__) return;
  window.__PC_CORE_LOADED__ = true;

  // --- Namespace public
  window.ProductCard = window.ProductCard || {};
  const ns = window.ProductCard;

  // ========== Utils ==========
  const qsa = (root, sel) => Array.from(root.querySelectorAll(sel));
  const qs = (root, sel) => root.querySelector(sel);
  const on = (root, evt, sel, handler) => {
    root.addEventListener(evt, e => {
      const target = e.target.closest(sel);
      if (target && root.contains(target)) handler(e, target);
    });
  };

  // ========== Helpers “métier” ==========
  const formatMoney = (price, currencyIso, currencySymbol) => {
    if (price == null) return '';

    // If we already received a formatted string (includes currency or any non-digit), keep it as-is.
    if (typeof price === 'string') {
      const trimmed = price.trim();
      if (/[^\d.,-]/.test(trimmed)) return trimmed;
      // numeric string: fall through to formatting
      price = Number(trimmed);
    }

    if (Number.isNaN(price)) return '';

    // Determine currency (prefer datasets / globals set by Liquid).
    const iso =
      currencyIso ||
      window.__ACTIVE_CURRENCY__ ||
      document.documentElement?.dataset?.currencyIso ||
      Shopify?.currency?.active ||
      (typeof ShopifyAnalytics !== 'undefined' ? ShopifyAnalytics?.meta?.currency : '') ||
      '';

    const symbol =
      currencySymbol ||
      window.__ACTIVE_CURRENCY_SYMBOL__ ||
      document.documentElement?.dataset?.currencySymbol ||
      (typeof Shopify !== 'undefined' ? Shopify?.currency?.symbol : '') ||
      '';

    // Prefer Intl with the active ISO to respect symbol for multi-currency (Global‑E, etc.).
    if (iso) {
      try {
        const config = {style: 'currency', currency : iso}
        if (currencySymbol === '$') {
          config.currency= iso
        }
        return new Intl.NumberFormat(undefined,config).format(Math.round(price) / 100);
      } catch (_) {}
    }

    // Fallback: Shopify formatter if available.
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      const formatter = Shopify?.currency?.active_format || Shopify?.money_format;
      return Shopify.formatMoney(Math.round(price), formatter);
    }

    // Symbol-only fallback.
    if (symbol) return `${(price / 100).toFixed(2)} ${symbol}`;

    // Fallback: simple decimals in case no symbol/formatter is available.
    return (price / 100).toFixed(2);
  };

  const extractColorCodeFromTags = (tags = []) => {
    for (let i = 0; i < tags.length; i++) {
      if (tags[i].indexOf('colorCode_') === 0) {
        return tags[i].replace('colorCode_', '');
      }
    }
    return null;
  };

  const toCents = (value) => {
    if (value == null) return null;

    if (typeof value === 'number') {
      return Math.round(value);
    }

    if (typeof value === 'string') {
      let normalized = value.trim().replace(/[^0-9,.\-]/g, '');
      // Convert comma decimals to dot, collapse multiple dots to keep only the last as decimal separator.
      normalized = normalized.replace(/,/g, '.');
      const parts = normalized.split('.');
      if (parts.length > 2) {
        const decimal = parts.pop();
        normalized = parts.join('') + '.' + decimal;
      }
      const parsed = parseFloat(normalized);
      if (!Number.isNaN(parsed)) {
        return Math.round(parsed * 100);
      }
    }

    return null;
  };

  const computePromoPercent = (price, compareAtPrice) => {
    const priceCents = toCents(price);
    const compareCents = toCents(compareAtPrice);

    if (priceCents == null || compareCents == null) return null;
    if (compareCents <= priceCents) return null;

    return Math.round(((compareCents - priceCents) / compareCents) * 100);
  };

  const isPrivateSales = (tags = []) => tags.includes('ventes_privees_E25');

  const MIN_CARD_WIDTH_FOR_SIZES = 260;

  function toggleDesktopSizesVisibility(card, widthOverride) {
    const sizes = qs(card, '.product-sizes');
    if (!sizes) return;
    const width = typeof widthOverride === 'number' && widthOverride > 0
      ? widthOverride
      : (card.getBoundingClientRect?.().width || card.offsetWidth || 0);
    const availableWidth = width || card.clientWidth || 0;
    const overflow = sizes.scrollWidth - availableWidth > 4;
    const shouldHide = availableWidth < MIN_CARD_WIDTH_FOR_SIZES || overflow;
    sizes.classList.toggle('product-sizes--hidden', shouldHide);
  }

  function setupDesktopSizesResponsiveness(container = document) {
    const cards = qsa(container, '.product-card');
    if (!cards.length) return;

    if ('ResizeObserver' in window) {
      if (!ns.__sizesResizeObserver) {
        ns.__sizesResizeObserver = new ResizeObserver(entries => {
          entries.forEach(entry => toggleDesktopSizesVisibility(entry.target, entry.contentRect?.width));
        });
      }
      cards.forEach(card => {
        if (card.dataset.pcSizesObserved) return;
        card.dataset.pcSizesObserved = '1';
        ns.__sizesResizeObserver.observe(card);
        toggleDesktopSizesVisibility(card);
      });
    } else {
      cards.forEach(card => toggleDesktopSizesVisibility(card));
      if (!ns.__sizesResizeFallbackBound) {
        ns.__sizesResizeFallbackBound = true;
        window.addEventListener('resize', () => {
          window.requestAnimationFrame(() => {
            document.querySelectorAll('.product-card').forEach(card => toggleDesktopSizesVisibility(card));
          });
        }, { passive: true });
      }
    }
  }

  // ========== Core: mise à jour d’une product-card ==========
  function updateProductCard(productCard, productData) {
    const productTitle     = qs(productCard, '.product-card__title');
    const productPrice     = qs(productCard, '.product-card__price-item');
    const productOldPrice  = qs(productCard, '.product-card__price-item-old');
    const productPromo     = qs(productCard, '#promo-span');
    const pointRouge       = qs(productCard, '#point-rouge-span');
    const productLink      = qs(productCard, '.product-card__link');
    const productImages    = qsa(productCard, '.product-card__images img');
    const productMobileImg = qsa(productCard, '.product-card__img');

    const priceCents = (productData.price);
    const compareCents = (productData.compare_at_price);
    const promoPercent = isPrivateSales(productData.tags)
      ? null
      : computePromoPercent(priceCents ?? productData.price, compareCents ?? productData.compare_at_price);

    if (productTitle) productTitle.textContent = productData.title || '';
    if (productLink)  productLink.href = '/products/' + (productData.handle || '');

    // Prix
    if (productPrice) {
      if (isPrivateSales(productData.tags)) {
        const discounted = priceCents != null ? Math.ceil(priceCents * 0.7) : productData.price;
        productPrice.textContent = formatMoney(discounted, productCard?.dataset?.currencyIso, productCard?.dataset?.currencySymbol);
      } else {
        const priceValue = priceCents != null ? priceCents : productData.price;
        productPrice.textContent = formatMoney(priceValue, productCard?.dataset?.currencyIso, productCard?.dataset?.currencySymbol);
      }
    }
    if (productOldPrice) {
      if (isPrivateSales(productData.tags)) {
        const base = priceCents != null ? priceCents : productData.price;
        productOldPrice.textContent = formatMoney(base, productCard?.dataset?.currencyIso, productCard?.dataset?.currencySymbol);
        productOldPrice.style.display = 'block';
      } else if (promoPercent) {
        const compareValue = compareCents != null ? compareCents : productData.compare_at_price;
        productOldPrice.textContent = formatMoney(compareValue, productCard?.dataset?.currencyIso, productCard?.dataset?.currencySymbol);
        productOldPrice.style.display = 'block';
      } else {
        productOldPrice.textContent = '';
        productOldPrice.style.display = 'none';
      }
    }
    if (productPromo) {
      if (isPrivateSales(productData.tags)) {
        productPromo.style.display = 'block';
        productPromo.textContent = '- 30%';
      } else if (promoPercent) {
        productPromo.style.display = 'block';
        productPromo.textContent = `- ${promoPercent}%`;
      } else {
        productPromo.style.display = 'none';
      }
    }
    if (pointRouge) {
      pointRouge.style.display = productData.tags?.includes('point_rouge') ? 'block' : 'none';
    }

    // Images (desktop + mobile), on enlève lazy attrs pour forcer le swap
    if (productImages[0]) {
      productImages[0].src = productData.featured_image || productData.images?.[0] || productImages[0].src;
      productImages[0].removeAttribute('srcset');
      productImages[0].removeAttribute('data-srcset');
    }
    if (productImages[1]) {
      setTimeout(() => {
        productImages[1].src = productData.images?.[1] || productData.featured_image || productImages[1].src;
        productImages[1].removeAttribute('srcset');
        productImages[1].removeAttribute('data-srcset');
        productImages[1].removeAttribute('loading');
      }, 50);
    }
    if (productMobileImg?.length) {
      productMobileImg.forEach((img, i) => {
        img.src = productData.images?.[i] || productData.featured_image || img.src;
        img.removeAttribute('srcset');
        img.removeAttribute('data-srcset');
      });
    }

    // Bouton “choisir une taille” (datasets pour le drawer mobile)
    const btn = qs(productCard, '.product-card__btn');
if (btn) {
  // 1) empêcher un submit fantôme sur iOS (si le bouton est dans un <form>)
  btn.setAttribute('type', 'button');

  // 2) (ré)écrire data-sizes + data-variants à CHAQUE update (après swatch, etc.)
  const sizesList = (productData.variants || []).map(v => v.option1).filter(Boolean);
  btn.setAttribute('data-sizes', sizesList.join(','));

  const variantsData = (productData.variants || []).map(v => `${v.id}:${v.option1}:${!!v.available}`);
  btn.setAttribute('data-variants', variantsData.join(','));

  // 3) le reste comme avant
  btn.setAttribute('data-title', productData.title || '');

  btn.setAttribute('data-price', productData.price);

  const promo = computePromoPercent(productData.price, productData.compare_at_price);
  if (promo) btn.setAttribute('data-promo', promo); else btn.removeAttribute('data-promo');

  if (productData.compare_at_price) {
    const old = isPrivateSales(productData.tags)
      ? (Math.ceil(productData.price) * 0.7 / 100)
      : (productData.compare_at_price / 100);
    btn.setAttribute('data-old-price', productData.compare_at_price);
  } else {
    btn.removeAttribute('data-old-price');
  }
  btn.setAttribute('data-color', extractColorCodeFromTags(productData.tags) || '');
  btn.setAttribute('data-handle', productData.handle || '');
}


    // Tailles desktop
    const sizesContainer = qs(productCard, '.product-sizes__options');
    if (sizesContainer) {
      sizesContainer.innerHTML = '';
      (productData.variants || []).forEach(variant => {
        const size = variant.option1;
        if (variant.available) {
          sizesContainer.insertAdjacentHTML('beforeend', `
            <product-form data-product-id="${productData.id}" id="product-form-component-${productData.id}">
              <form method="post" class="add-to-cart-form" data-variant-id="${variant.id}" action="/cart/add">
                <input type="hidden" class="cardSizePicker" name="id" value="${variant.id}">
                <button type="submit" class="product-size">${size}</button>
              </form>
            </product-form>
          `);
        } else {
          sizesContainer.insertAdjacentHTML('beforeend', `
            <button class="product-size--disabled back-in-stock-button"
                    style="display:flex;align-items:center;justify-content:center;max-height:32px;"
                    data-variant='${JSON.stringify(variant)}'>
              ${size}
              <span class="bell-icon" aria-label="Notification disponible" style="margin-left:5px;">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-bell">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                  <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                </svg>
              </span>
            </button>
          `);
        }
      });
    }
  }

  // Expose pour la “glue” externe éventuelle (Nosto, etc.)
  window.updateProductCard = updateProductCard;

  // ========== Swatches couleur ==========
  function bindColorSwatchesOnce() {
    if (document.__pcSwatchesBound) return;
    document.__pcSwatchesBound = true;

    // délégation globale, une seule accroche
    document.addEventListener('click', (event) => {
      const swatch = event.target.closest('.color-swatch');
      if (!swatch) return;

      event.preventDefault();
      const newProduct = JSON.parse(swatch.getAttribute('data-product') || '{}');
      const productCardPrice = swatch.getAttribute('data-product-price');
      const productCardCompareAtPrice = swatch.getAttribute('data-product-compare_at_price');
      const productCard = swatch.closest('.product-card');
      if (!productCard || !newProduct) return;
      newProduct.price = productCardPrice
      newProduct.compare_at_price = productCardPrice === productCardCompareAtPrice ? null : productCardCompareAtPrice

      updateProductCard(productCard, newProduct);
      productCard.setAttribute('data-current-product', JSON.stringify(newProduct));
      productCard.setAttribute('data-current-product-price', productCardPrice);
      productCard.setAttribute('data-current-product-compare_at_price', productCardCompareAtPrice);

      // état visuel
      productCard.querySelectorAll('.color-swatch .color-circle').forEach(c => c.classList.remove('active'));
      const circle = swatch.querySelector('.color-circle');
      if (circle) circle.classList.add('active');
    }, { passive: false });
  }

  // ========== Drawer mobile tailles ==========
  function setupProductSizesDrawer(/* container non utilisé ici */) {
  // Accroche unique et globale (évite de rater des boutons arrivés plus tard)
  if (document.__sizesDrawerBound) return;
  document.__sizesDrawerBound = true;

  document.addEventListener('click', (evt) => {
  const button = evt.target.closest('.product-card__btn');
    if (!button) return;

    // on n'ouvre le drawer que si le bouton porte bien les datasets
    if (!button.hasAttribute('data-sizes')) return;

    // bloque toute navigation/submit parasite (très important sur mobile)
    evt.preventDefault();
    evt.stopPropagation();

    // ---- à partir d'ici, reprend ton code existant ----
    // Nettoie un drawer existant
    document.querySelectorAll('.product-sizes-overlay').forEach(el => el.remove());

    const sizes = (button.getAttribute('data-sizes') || '').split(',').filter(Boolean);
    const variantsData = (button.getAttribute('data-variants') || '').split(',').filter(Boolean);
    const productTitle = button.getAttribute('data-title') || '';
    let productPrice   = button.getAttribute('data-price') || '';
    let productPromo   = button.getAttribute('data-promo') || '';
    let productPriceOld= button.getAttribute('data-old-price') || '';
    const productColor = button.getAttribute('data-color') || '';
    const productHandle= button.getAttribute('data-handle') || '';
    const product = JSON.parse(button.closest('.product-card')?.getAttribute('data-current-product') || '{}');


    const variantsMap = {};
    const variantsAvailability = {};
    variantsData.forEach(pair => {
      const [id, size, available] = pair.split(':');
      variantsMap[size] = id;
      variantsAvailability[size] = available === 'true';
    });

    const overlay = document.createElement('div');
    overlay.className = 'product-sizes-overlay';

    const drawer = document.createElement('div');
    drawer.className = 'product-sizes-drawer';
    drawer.innerHTML = `
      <div class="product-sizes__close" aria-label="Fermer">✕</div>
      <h2 class="product-sizes__title">${window.I18N.header_title}</h2>
      <p class="product-sizes__subtitle">${productTitle}</p>
      <div class="prix__content">
        <p class="product-price">${productPrice}</p>
        <p class="reduction">${productPromo || ''}</p>
        <p class="product-price-old">${productPriceOld === productPrice ? '' : productPriceOld || ''}</p>
      </div>
      <div class="product-colors"></div>
      <div class="product-sizes__content">
        ${
          sizes.map(size => {
            const available = !!variantsAvailability[size];
            const cls = available
              ? 'product-size--mobile'
              : 'product-size--mobile product-size--disabled back-in-stock-button';
            const style = available
              ? ''
              : 'style="display:flex;align-items:center;justify-content:space-between;width:100%;text-align:left;"';
            const payload = available
              ? variantsMap[size]
              : JSON.stringify((product.variants || []).find(v => v.option1 === size));
            const bell = available ? '' : `
              <span class="bell-icon" aria-label="Notification disponible">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-bell">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                  <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                </svg>
              </span>`;
            return `<button type="button" class="${cls}" ${style}
                            data-variant='${payload}' data-product-id='${product.id}'>
                      ${size} ${bell}
                    </button>`;
          }).join('')
        }
      </div>
      <product-form data-product-id="${product.id}" id="product-form-component-${product.id}">
        <form method="post" class="add-to-cart-form" data-variant-id="" action="/cart/add" style="margin:0;">
          <button type="submit" class="add-to-cart" disabled>${window.I18N.select_button}</button>
          <input type="hidden" name="id" value="" data-product-form-variant id="mobileSizeVariantId">
        </form>
      </product-form>
    `;

    const reductionElement = drawer.querySelector('.reduction');
    if (productPromo === '') reductionElement.style.display = 'none';

    overlay.appendChild(drawer);
    document.body.appendChild(overlay);
    document.body.style.overflow = 'hidden';

    // ouverture animée fiable (meilleure qu'un setTimeout)
    requestAnimationFrame(() => drawer.classList.add('open'));

    // Couleurs (+n)
    const productCard = button.closest('.product-card');
    const colorElements = productCard ? productCard.querySelectorAll('.color-swatch') : [];
    const colors = new Map();
    colorElements.forEach(el => {
      const pdata = JSON.parse(el.getAttribute('data-product') || '{}');
      const colorTag = (pdata.tags || []).find(t => t.startsWith('colorCode_'));
      if (colorTag) {
        const colorCode = colorTag.replace('colorCode_', '');
        const url = pdata.url || (pdata.handle ? `/products/${pdata.handle}` : '#');
        colors.set(colorCode, url);
      }
    });
    const colorContainer = drawer.querySelector('.product-colors');
    const maxColorsToShow = 6;
    let shown = 0;
    if (productColor) {
      const selected = document.createElement('div');
      selected.className = 'color-option-link selected';
      const dot = document.createElement('div');
      dot.className = 'color-option';
      dot.style.backgroundColor = productColor;
      selected.appendChild(dot);
      colorContainer.appendChild(selected);
      shown++;
    }
    const hidden = [];
    colors.forEach((url, color) => {
      if (shown < maxColorsToShow) {
        if (color === productColor) return;
        const a = document.createElement('a');
        a.href = url;
        a.className = 'color-option-link';
        const dot = document.createElement('div');
        dot.className = 'color-option';
        dot.style.backgroundColor = color;
        a.appendChild(dot);
        colorContainer.appendChild(a);
        shown++;
      } else {
        hidden.push({ color, url });
      }
    });
    if (hidden.length) {
      const more = document.createElement('a');
      more.href = productHandle ? `/products/${productHandle}` : '#';
      more.className = 'color-option more-colors';
      more.textContent = `+${hidden.length}`;
      colorContainer.appendChild(more);
    }

    // sélection + ATC
    const sizeButtons = drawer.querySelectorAll('.product-size--mobile');
    const addToCartButton = drawer.querySelector('.add-to-cart');
    const inputHidden = drawer.querySelector('#mobileSizeVariantId');

    sizeButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        sizeButtons.forEach(b => b.classList.remove('selected'));
        if (!btn.classList.contains('product-size--disabled')) {
          btn.classList.add('selected');
          inputHidden.value = btn.getAttribute('data-variant') || '';
          addToCartButton.disabled = !inputHidden.value;
          addToCartButton.textContent = window.I18N.add_to_cart;
        }
      });
    });

    const close = () => {
      drawer.classList.remove('open');
      setTimeout(() => {
        overlay.remove();
        document.body.style.overflow = '';
      }, 350);
    };
    drawer.querySelector('.product-sizes__close')?.addEventListener('click', close);
    overlay.addEventListener('click', (ev) => { if (ev.target === overlay) close(); });
  });
}


  // ========== Extras (facultatifs mais utiles sur collection) ==========
  function portableUIBits(container) {
    // Bouton filtres sticky
    const filterWrapper = qs(container, '.shop__filters-btn-wrapper');
    if (filterWrapper && !filterWrapper.dataset.pcStickyInited) {
      filterWrapper.dataset.pcStickyInited = '1';
      const originalOffset = filterWrapper.getBoundingClientRect().top + window.scrollY;
      const onScroll = () => {
        if (window.scrollY > originalOffset - 140) filterWrapper.classList.add('sticky');
        else filterWrapper.classList.remove('sticky');
      };
      window.addEventListener('scroll', onScroll, { passive: true });
    }

    // Rich text vide -> hide
    const section = qs(container, '[id^="shopify-section-"][id$="rich_text"]');
    if (section && !section.dataset.pcHideEmptyInited) {
      section.dataset.pcHideEmptyInited = '1';
      const richTextContent = qs(section, '.rich-text__content');
      if (richTextContent && richTextContent.textContent.trim() === '') {
        section.style.display = 'none';
      }
    }
  }

  // ========== Public: ns.init(container) ==========
  ns.init = (container = document) => {
    // éviter réinit multiple sur la même card
    qsa(container, '.product-card').forEach(card => {
      if (!card.dataset.pcInitialized) {
        // si la card a déjà un produit courant, on s’assure que le DOM est en phase
        const current = card.getAttribute('data-current-product');
        const currentPrice = card.getAttribute('data-current-product-price');
        const currentCompareAtPrice = card.getAttribute('data-current-product-compare_at_price');
        if (current) {
          const data =  JSON.parse(current)
          data.price = currentPrice
          data.compare_at_price = currentPrice === currentCompareAtPrice ? null : currentCompareAtPrice
          console.log(currentPrice , currentCompareAtPrice, currentPrice === currentCompareAtPrice ? null : currentCompareAtPrice)
          try { updateProductCard(card, data); } catch {}
        }
        card.dataset.pcInitialized = '1';
      }
    });

    bindColorSwatchesOnce();
    setupProductSizesDrawer(container);
    portableUIBits(container);
    setupDesktopSizesResponsiveness(container);
  };

  // ========== Boot & ré-inits légers ==========
  const boot = () => ns.init(document);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Navigation Shopify & sections
  window.addEventListener('pageshow', () => ns.init(document), { passive: true });
  document.addEventListener('shopify:section:load', (e) => ns.init(e.target));

  // Observer léger : ne réagit qu’aux ajouts de .product-card / .shop__products
  const bodyMO = new MutationObserver((muts) => {
    let needs = false;
    for (const m of muts) {
      for (const node of m.addedNodes) {
        if (!(node instanceof HTMLElement)) continue;
        if (node.matches?.('.product-card, .shop__products')) { needs = true; break; }
        if (node.querySelector?.('.product-card, .shop__products')) { needs = true; break; }
      }
      if (needs) break;
    }
    if (needs) {
      // dé-bounce via rAF
      if (bodyMO.__raf) cancelAnimationFrame(bodyMO.__raf);
      bodyMO.__raf = requestAnimationFrame(() => ns.init(document));
    }
  });
  bodyMO.observe(document.body, { childList: true, subtree: true });

})();
